import React, { useEffect, useState } from 'react';
import { Mail, Plus, X, Loader2, Save } from 'lucide-react';
import { toast } from 'sonner';
import api from '../../services/api';
import { authHeaders } from './gradeUtils';

const inputCls = 'bg-black border-2 border-zinc-800 focus:border-orange-500 outline-none px-3 py-2 text-xs text-white';

export const ParentEmailsEditor = ({ studentId }) => {
  const [main, setMain] = useState('');
  const [extras, setExtras] = useState([]);
  const [draft, setDraft] = useState('');
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    api.get(`/academy/admin/students/${studentId}/parent-emails`, { headers: authHeaders() })
      .then((r) => { setMain(r.data.parent_email); setExtras(r.data.additional_parent_emails); })
      .catch(() => toast.error('Could not load parent emails'));
  }, [studentId]);

  const addDraft = () => {
    const e = draft.trim().toLowerCase();
    if (!e) return;
    if (!extras.includes(e) && e !== main) setExtras([...extras, e]);
    setDraft('');
  };

  const save = async () => {
    setBusy(true);
    try {
      const pending = draft.trim() ? [...extras, draft.trim().toLowerCase()] : extras;
      const r = await api.put(`/academy/admin/students/${studentId}/parent-emails`, { parent_email: main, additional_parent_emails: pending }, { headers: authHeaders() });
      setMain(r.data.parent_email); setExtras(r.data.additional_parent_emails); setDraft('');
      toast.success(r.data.welcomed.length ? `Saved · Family Hub link sent to ${r.data.welcomed.join(', ')}` : 'Parent emails saved');
    } catch (e) { toast.error(e?.response?.data?.detail || 'Save failed'); }
    finally { setBusy(false); }
  };

  return (
    <div className="border-2 border-zinc-800 p-4 mb-6" data-testid="parent-emails-editor">
      <div className="flex items-center gap-2 mb-3"><Mail className="w-4 h-4 text-orange-400" /><span className="text-xs font-black uppercase tracking-widest">Parent Emails</span><span className="text-[10px] text-zinc-500">· all receive the daily parent email</span></div>
      <div className="flex flex-wrap items-center gap-2">
        <span className="text-[9px] font-black uppercase tracking-widest text-zinc-500 w-14">Main</span>
        <input className={`${inputCls} flex-1 min-w-[200px]`} value={main} onChange={(e) => setMain(e.target.value)} placeholder="parent@email.com" data-testid="parent-email-main" />
      </div>
      {extras.map((e) => (
        <div key={e} className="flex items-center gap-2 mt-2" data-testid={`parent-email-extra-${e}`}>
          <span className="text-[9px] font-black uppercase tracking-widest text-zinc-500 w-14">Also</span>
          <span className="flex-1 text-xs text-zinc-200 border-2 border-zinc-900 px-3 py-2 truncate">{e}</span>
          <button onClick={() => setExtras(extras.filter((x) => x !== e))} className="p-2 text-zinc-500 hover:text-red-400" data-testid={`parent-email-remove-${e}`}><X className="w-4 h-4" /></button>
        </div>
      ))}
      <div className="flex flex-wrap items-center gap-2 mt-2">
        <span className="text-[9px] font-black uppercase tracking-widest text-zinc-500 w-14">Add</span>
        <input className={`${inputCls} flex-1 min-w-[200px]`} value={draft} onChange={(e) => setDraft(e.target.value)} onKeyDown={(e) => e.key === 'Enter' && addDraft()} placeholder="another parent's email" data-testid="parent-email-add-input" />
        <button onClick={addDraft} className="p-2 border-2 border-zinc-800 hover:border-orange-500 text-zinc-300" data-testid="parent-email-add-btn"><Plus className="w-4 h-4" /></button>
        <button onClick={save} disabled={busy} className="flex items-center gap-1.5 bg-orange-500 hover:bg-orange-400 text-black px-4 py-2 text-[10px] font-black uppercase tracking-widest disabled:opacity-50" data-testid="parent-emails-save-btn">
          {busy ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Save className="w-3.5 h-3.5" />} Save
        </button>
      </div>
      <p className="text-[10px] text-zinc-600 mt-2">Newly added parents automatically get a Family Hub link by email.</p>
    </div>
  );
};
